import { api } from '../lib/api'

// Types (mirrored from mobile, trimmed for web MVP)
export type CursorPage<T> = {
  results: T[]
  next: string | null
  previous?: string | null
}

export type MiniActor = {
  id: number | string
  name: string
  avatarUrl?: string | null
}

export type ThreadListItem = {
  id: number
  title: string
  dmPeer?: MiniActor | null
  participants: Array<{ id: number | string; name: string; avatarUrl?: string | null }>
  lastMessage: { id: number; text: string; createdAt: string } | null
  unreadCount: number
  updatedAt: string
  pinned?: boolean
}

export type MessageAttachment = { kind: 'file' | 'image' | string; url: string }

export type MessageItem = {
  id: number | string
  fromMe: boolean
  actor: MiniActor | null
  text: string
  ciphertext?: string
  createdAt: string
  pending?: boolean
  attachments?: MessageAttachment[]
}

// Notifications (shared with mobile)
export type NotificationActor = {
  id: number | string
  name: string
  username?: string | null
  avatarUrl?: string | null
}

export type NotificationFeedItem = {
  id: string
  type: string
  verb: string
  preview?: string | null
  actor?: NotificationActor | null
  deeplink?: string | null
  postId?: number | string | null
  targetId?: string | null
  targetType?: string | null
  createdAt: string
  read: boolean
  category: string
  eventType: string
  metadata: Record<string, any>
  raw: any
}

function toISO(v?: string | null) {
  if (!v) return new Date().toISOString()
  const d = new Date(v)
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
}

function normActor(a: any): MiniActor | null {
  if (!a) return null
  return {
    id: Number(a.id ?? a.user_id ?? a.userId ?? a.pk ?? 0),
    name: a.name ?? a.display_name ?? a.username ?? '',
    avatarUrl: a.avatarUrl ?? a.avatar_url ?? null,
  }
}

function normThread(row: any): ThreadListItem {
  const last = row.lastMessage ?? row.last_message ?? null
  const lastMsg =
    last && typeof last === 'object'
      ? {
          id: Number(last.id),
          text: last.text ?? '',
          createdAt: toISO(last.createdAt ?? last.created_at),
        }
      : null

  const participants = Array.isArray(row.participants)
    ? (row.participants.map((p: any) => normActor(p)).filter(Boolean) as MiniActor[])
    : []

  const dmPeer = row.dm_peer ? normActor(row.dm_peer) : null

  return {
    id: Number(row.id),
    title: row.title ?? '',
    dmPeer,
    participants,
    lastMessage: lastMsg,
    unreadCount: Number(row.unreadCount ?? row.unread_count ?? 0),
    updatedAt: toISO(row.updatedAt ?? row.updated_at ?? lastMsg?.createdAt),
    pinned: Boolean(row.pinned ?? row.is_pinned ?? false),
  }
}

function normMessage(m: any, meId?: number | string): MessageItem {
  const created = m.createdAt ?? m.created_at
  const actor = normActor(m.actor ?? m.sender)
  const fromMe = m.fromMe ?? (meId != null ? m?.sender_id === meId || m?.senderId === meId : false)

  return {
    id: m.id,
    fromMe: Boolean(fromMe),
    actor,
    text: m.text ?? '',
    ciphertext: m.ciphertext ?? '',
    createdAt: toISO(created),
    attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
  }
}

function extractCursor(nextUrl?: string | null): string | null {
  if (!nextUrl || typeof nextUrl !== 'string') return null
  const m = nextUrl.match(/[?&]cursor=([^&#]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

// Threads (Inbox)
export async function getThreads(cursor?: string, opts?: { q?: string }): Promise<CursorPage<ThreadListItem>> {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  if (opts?.q && opts.q.trim()) params.set('q', opts.q.trim())
  const qs = params.toString()
  const url = `/api/inbox/threads/${qs ? `?${qs}` : ''}`
  const { data } = await api.get(url)
  const page: CursorPage<ThreadListItem> = {
    results: Array.isArray(data?.results) ? data.results.map(normThread) : [],
    next: data?.next ? extractCursor(data.next) : null,
    previous: data?.previous ? extractCursor(data.previous) : null,
  }
  return page
}

export async function createOrReuseThread(p: { members: number[]; title?: string; isSecure?: boolean }): Promise<{ threadId: number }> {
  const body = { members: p.members, title: p.title ?? '', is_secure: Boolean(p.isSecure) }
  const { data } = await api.post(`/api/inbox/threads/`, body)
  return { threadId: Number(data?.threadId) }
}

export async function lookupThread(members: number[]): Promise<{ threadId: number | null }> {
  const qs = members.join(',')
  const { data } = await api.get(`/api/inbox/threads/lookup/?members=${encodeURIComponent(qs)}`)
  return { threadId: data?.threadId != null ? Number(data.threadId) : null }
}

export async function resolveRecipientIdentity(q: string): Promise<{ userId: number; username?: string; handle?: string }> {
  const query = String(q || '').trim()
  const { data } = await api.get(`/api/inbox/recipients/resolve/?q=${encodeURIComponent(query)}`)
  return {
    userId: Number(data?.userId),
    username: data?.username ? String(data.username) : undefined,
    handle: data?.handle ? String(data.handle) : undefined,
  }
}

export type RecipientSuggestion = {
  userId: number
  handle: string
  username?: string
  displayName?: string
}

export async function suggestRegisteredRecipients(q: string, limit = 6): Promise<RecipientSuggestion[]> {
  const query = String(q || '').trim()
  if (query.length < 2) return []
  const { data } = await api.get(`/api/inbox/recipients/suggest/?q=${encodeURIComponent(query)}&limit=${encodeURIComponent(String(limit))}`)
  const rows = Array.isArray(data?.results) ? data.results : []
  return rows
    .map((row: any) => ({
      userId: Number(row?.userId),
      handle: String(row?.handle || ''),
      username: row?.username ? String(row.username) : undefined,
      displayName: row?.displayName ? String(row.displayName) : undefined,
    }))
    .filter((row: RecipientSuggestion) => Number.isFinite(row.userId) && row.userId > 0 && row.handle.trim().length > 0)
}

export async function deleteThread(threadId: number | string): Promise<void> {
  await api.delete(`/api/inbox/threads/${threadId}/`)
}

export async function updateLastRead(threadId: number | string): Promise<{ ok: boolean }> {
  const { data } = await api.post(`/api/inbox/threads/${threadId}/read/`, {})
  return { ok: !!data?.ok }
}

export async function pinThread(threadId: number | string): Promise<void> {
  await api.post(`/api/inbox/threads/${threadId}/pin/`)
}

export async function unpinThread(threadId: number | string): Promise<void> {
  await api.delete(`/api/inbox/threads/${threadId}/pin/`)
}

// Messages (Thread)
export async function getThreadMessages(
  threadId: number | string,
  cursor?: string,
  meId?: number | string
): Promise<CursorPage<MessageItem>> {
  const url = cursor
    ? `/api/inbox/threads/${threadId}/messages/?cursor=${encodeURIComponent(cursor)}`
    : `/api/inbox/threads/${threadId}/messages/`
  const { data } = await api.get(url)
  const results = Array.isArray(data?.results) ? data.results.map((m: any) => normMessage(m, meId)) : []
  return { results, next: data?.next ? extractCursor(data.next) : null, previous: data?.previous ? extractCursor(data.previous) : null }
}

export async function sendMessage(
  threadId: number | string,
  body: { text?: string; ciphertext?: string; attachments?: MessageAttachment[] },
  meId?: number | string
): Promise<MessageItem> {
  const payload: any = { text: body.text ?? '', ciphertext: body.ciphertext ?? '' }
  if (body.attachments?.length) payload.attachments = body.attachments
  const { data } = await api.post(`/api/inbox/threads/${threadId}/messages/`, payload)
  return normMessage(data, meId)
}

// Notifications (full feed, aligned with mobile)
const deriveNotificationType = (eventType?: string, category?: string, metadata?: Record<string, any>) => {
  const evt = (eventType || '').toLowerCase()
  const cat = (category || '').toLowerCase()
  if (evt.includes('mention')) return 'mention'
  if (evt.includes('reaction')) return 'reaction'
  if (evt.includes('follow')) return 'follow'
  if (evt.startsWith('comment.')) return 'comment'
  if (evt.startsWith('message.')) return 'message'
  if (evt.startsWith('call.')) return 'call'
  if (evt.startsWith('order.') || cat === 'commerce') return 'order'
  if (evt.startsWith('wallet.')) return 'wallet'
  if (evt.startsWith('gift.')) return 'gift'
  if (metadata?.call_type) return 'call'
  return category || 'system'
}

const derivePostId = (row: any) => {
  if (row?.metadata?.post_id != null) return row.metadata.post_id
  if (row?.metadata?.postId != null) return row.metadata.postId
  const target = (row?.target_type || '').toLowerCase()
  if (target.includes('post') && row?.target_id != null) {
    const num = Number(row.target_id)
    return Number.isNaN(num) ? row.target_id : num
  }
  return null
}

const normNotificationActor = (actor: any): NotificationActor | null => {
  if (!actor) return null
  return {
    id: actor.id ?? actor.user_id ?? actor.userId ?? actor.pk ?? 0,
    name: actor.name ?? actor.display_name ?? actor.username ?? '',
    username: actor.username ?? null,
    avatarUrl: actor.avatarUrl ?? actor.avatar_url ?? null,
  }
}

const normNotification = (row: any): NotificationFeedItem => {
  const type = deriveNotificationType(row?.event_type, row?.category, row?.metadata)
  const preview = row?.preview_text ?? row?.preview ?? row?.metadata?.preview ?? row?.metadata?.note ?? null
  return {
    id: String(row?.id),
    type,
    verb: row?.verb ?? '',
    preview,
    actor: normNotificationActor(row?.actor),
    deeplink: row?.deeplink ?? null,
    postId: derivePostId(row),
    targetId: row?.target_id ?? null,
    targetType: row?.target_type ?? null,
    createdAt: toISO(row?.created_at ?? row?.createdAt),
    read: Boolean(row?.read),
    category: row?.category ?? 'system',
    eventType: row?.event_type ?? '',
    metadata: row?.metadata ?? {},
    raw: row,
  }
}

export async function getNotifications(cursor?: string, opts?: { q?: string; type?: string }): Promise<CursorPage<NotificationFeedItem>> {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  if (opts?.q && opts.q.trim()) params.set('q', opts.q.trim())
  if (opts?.type && opts.type.trim()) params.set('type', opts.type.trim())
  const qs = params.toString()
  const url = `/api/notifications/${qs ? `?${qs}` : ''}`
  const { data } = await api.get(url)
  const results = Array.isArray(data?.results) ? data.results.map(normNotification) : []
  return {
    results,
    next: data?.next ? extractCursor(data.next) : null,
    previous: data?.previous ? extractCursor(data.previous) : null,
  }
}

export async function markNotificationsRead(ids: Array<number | string>): Promise<{ updated: number }> {
  const { data } = await api.post(`/api/notifications/mark-read/`, { ids })
  return { updated: Number(data?.updated ?? 0) }
}

// Delete a single notification via notifications app (supports destroy)
export async function deleteNotification(id: number | string): Promise<void> {
  await api.delete(`/api/notifications/${id}/`)
}

// Fetch single notification detail
export async function getNotification(id: number | string): Promise<NotificationFeedItem> {
  const { data } = await api.get(`/api/notifications/${id}/`)
  return normNotification(data)
}

// Fetch unread count for nav badge (notifications app)
export async function getNotificationCount(): Promise<{ unread: number }> {
  const { data } = await api.get(`/api/notifications/count/`)
  const unread = Number(data?.unread ?? 0)
  return { unread: Number.isFinite(unread) ? unread : 0 }
}
